import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gem, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UpgradeModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  featureName: string;
  subscription: 'free' | 'standard' | 'premium' | 'banned';
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onOpenChange, featureName, subscription }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/pricing');
  };

  const handleTopUp = () => {
    onOpenChange(false);
    // TODO: Navigate to ChefCoin purchase/top-up page when available
    navigate('/buy-chefcoins');
  };

  const isPremium = subscription === 'premium';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <Gem className="h-6 w-6 text-purple-500" />
            <DialogTitle>{isPremium ? 'Out of ChefCoins' : 'Unlock More Power!'}</DialogTitle>
          </div>
          <DialogDescription>
            {isPremium ? (
              <>
                You've run out of ChefCoins for {featureName}. As a Premium member, you can top up your ChefCoins any time to keep using advanced features.<br />
                <strong>Tip:</strong> ChefCoins are required for premium tools like Recipe Studio and AI image generation.
              </>
            ) : (
              <>
                You've run out of ChefCoins for {featureName}. Upgrade to a Premium plan to get <strong>2,000 ChefCoins</strong> every month and unleash your full culinary potential!
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Maybe Later</Button>
          {isPremium ? (
            <Button onClick={handleTopUp} className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-600 text-white">
              <Gem className="h-4 w-4 mr-2" />
              Buy More ChefCoins
            </Button>
          ) : (
            <Button onClick={handleUpgrade} className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700">
              <Zap className="h-4 w-4 mr-2" />
              Upgrade to Premium
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};